import httpService from "./httpService";
import type {
  AdminEmailBackfillResponse,
  AdminUserContactListParams,
  AdminUserContactListResponse,
  CommunicationEndpoint,
  CommunicationEndpointsResponse,
  NotificationCommunicationEndpointType,
  NotificationUserHistoryParams,
  NotificationUserHistoryResponse,
} from "../types/adminNotifications";
import type { NotificationPreferencePatch, NotificationPreferencesResponse } from "../types/notifications";

/**
 * ADMIN-only notification contact management (PWA-2690, PWA-2748), wrapping
 * the settings gateway routes under `/v3/admin/users/...` (plutus-backend
 * `routes/v3/admin/adminNotificationSettingsRoutes.ts`).
 *
 * - The target user is always the path segment; request bodies never carry a
 *   user ID (the gateway derives the acting admin from the JWT).
 * - Endpoint values are sensitive: callers must not log them or put them in URLs.
 * - Every call is actor-scoped (`requestScope: "actor"`): a delegated account
 *   context must not re-route these admin operations.
 * - The bulk update endpoint is intentionally not wrapped (no UI uses it).
 */

const ADMIN_USERS_BASE = "/v3/admin/users";

const ACTOR_AUTH = { requiresAuth: true, requestScope: "actor" as const };

const userSegment = (userId: string): string => `${ADMIN_USERS_BASE}/${encodeURIComponent(userId)}`;

const userPath = (userId: string): string => `${userSegment(userId)}/communication-endpoints`;

const CONTACT_TEXT_FILTERS = ["userId", "name", "accountEmail", "communicationEmail", "sms", "q", "ibkrAccountId"] as const;

const HISTORY_SCALAR_FIELDS = ["page", "pageSize", "fromUtc", "toUtc", "categoryKey", "subcategoryKey", "outcome"] as const;

const toPatchBody = (patch: NotificationPreferencePatch): NotificationPreferencePatch => {
  const entry: NotificationPreferencePatch = {
    categoryKey: patch.categoryKey,
    channel: patch.channel,
    enabled: patch.enabled,
  };
  if (patch.subcategoryKey !== undefined) entry.subcategoryKey = patch.subcategoryKey;
  return entry;
};

class NotificationContactsService {
  /** GET a user's notification email/SMS destinations; each may be null. 404 for a missing user. */
  async getUserEndpoints(userId: string): Promise<CommunicationEndpointsResponse> {
    const response = await httpService.get<CommunicationEndpointsResponse>(userPath(userId), ACTOR_AUTH);
    return response.data;
  }

  /** Replace the notification email only (login email is untouched). Clears verifiedAtUtc. */
  async replaceUserEmail(userId: string, email: string): Promise<CommunicationEndpoint> {
    const response = await httpService.put<CommunicationEndpoint>(
      `${userPath(userId)}/email`,
      { email },
      ACTOR_AUTH
    );
    return response.data;
  }

  /** Set or replace the E.164 SMS number. Clears verifiedAtUtc. */
  async replaceUserSms(userId: string, phoneNumber: string): Promise<CommunicationEndpoint> {
    const response = await httpService.put<CommunicationEndpoint>(
      `${userPath(userId)}/sms`,
      { phoneNumber },
      ACTOR_AUTH
    );
    return response.data;
  }

  /** Remove the SMS number. 204 with no body, idempotent for an existing user. */
  async removeUserSms(userId: string): Promise<void> {
    await httpService.delete<unknown>(`${userPath(userId)}/sms`, ACTOR_AUTH);
  }

  /**
   * Paginated contact directory. Only the documented query fields are sent and
   * blank filters are omitted; `+` in the sms filter is percent-encoded by
   * URLSearchParams as the API requires.
   */
  async listContacts(params: AdminUserContactListParams = {}): Promise<AdminUserContactListResponse> {
    const query = new URLSearchParams();
    if (params.page !== undefined) query.set("page", String(params.page));
    if (params.pageSize !== undefined) query.set("pageSize", String(params.pageSize));
    if (params.hasIbkrAccountId !== undefined) query.set("hasIbkrAccountId", String(params.hasIbkrAccountId));
    if (params.hasCommunicationEmail !== undefined) query.set("hasCommunicationEmail", String(params.hasCommunicationEmail));
    if (params.hasCommunicationSms !== undefined) query.set("hasCommunicationSms", String(params.hasCommunicationSms));
    if (params.communicationEmailVerified !== undefined) {
      query.set("communicationEmailVerified", String(params.communicationEmailVerified));
    }
    if (params.communicationSmsVerified !== undefined) {
      query.set("communicationSmsVerified", String(params.communicationSmsVerified));
    }
    CONTACT_TEXT_FILTERS.forEach(key => {
      const value = params[key]?.trim();
      if (value) query.set(key, value);
    });
    const queryString = query.toString();
    const url = `${ADMIN_USERS_BASE}/communication-endpoints${queryString ? `?${queryString}` : ""}`;
    const response = await httpService.get<AdminUserContactListResponse>(url, ACTOR_AUTH);
    return response.data;
  }

  /**
   * Copy login emails into missing notification-email endpoints. `dryRun`
   * reports counts without writing. Existing endpoints are never modified.
   */
  async backfillEmails(dryRun: boolean): Promise<AdminEmailBackfillResponse> {
    const response = await httpService.post<AdminEmailBackfillResponse>(
      `${ADMIN_USERS_BASE}/communication-endpoints/email/backfill?dryRun=${dryRun ? "true" : "false"}`,
      undefined,
      ACTOR_AUTH
    );
    return response.data;
  }

  /** ADMIN attestation: mark a user's email or SMS verified (or clear it) without sending a code. */
  async setUserEndpointVerified(
    userId: string,
    endpointType: NotificationCommunicationEndpointType,
    verified: boolean
  ): Promise<CommunicationEndpoint> {
    const response = await httpService.put<CommunicationEndpoint>(
      `${userPath(userId)}/${endpointType.toLowerCase()}/verification`,
      { verified },
      ACTOR_AUTH
    );
    return response.data;
  }

  /**
   * Every message addressed to one user (admin and automated), newest first,
   * each with that user's own outcome and deliveries. 404 for a missing user.
   */
  async listUserHistory(userId: string, params: NotificationUserHistoryParams = {}): Promise<NotificationUserHistoryResponse> {
    const query = new URLSearchParams();
    HISTORY_SCALAR_FIELDS.forEach(field => {
      const value = params[field];
      if (value === undefined || value === null) return;
      const text = String(value).trim();
      if (text) query.set(field, text);
    });
    if (params.channels && params.channels.length > 0) query.set("channels", params.channels.join(","));
    const queryString = query.toString();
    const response = await httpService.get<NotificationUserHistoryResponse>(
      `${userSegment(userId)}/notifications${queryString ? `?${queryString}` : ""}`,
      ACTOR_AUTH
    );
    return response.data;
  }

  /** Resolved preference tree for one user; same shape as the self-service contract. */
  async getUserPreferences(userId: string): Promise<NotificationPreferencesResponse> {
    const response = await httpService.get<NotificationPreferencesResponse>(
      `${userSegment(userId)}/notification-preferences`,
      ACTOR_AUTH
    );
    return response.data;
  }

  /** Administrator override of a user's preferences; audited per changed cell on the backend. */
  async updateUserPreferences(
    userId: string,
    patch: NotificationPreferencePatch[]
  ): Promise<NotificationPreferencesResponse> {
    const response = await httpService.put<NotificationPreferencesResponse>(
      `${userSegment(userId)}/notification-preferences`,
      { preferences: patch.map(toPatchBody) },
      ACTOR_AUTH
    );
    return response.data;
  }
}

export const notificationContactsService = new NotificationContactsService();
export default notificationContactsService;
